import { useEffect, useRef } from 'react'
import { InfoCircleFilled } from '@/assets/icons'
import { selectSettingsLoaded } from '@/chat-common/store/chat-ux.selectors'
import { JurisdictionIcon } from '@/jurisdiction-mappings/components/JurisdictionIcon'
import { getJurisdictionLabel, inferJurisdictionKind } from '@/jurisdiction-mappings/jurisdiction-maps'
import type { MatterRead } from '@/matter/store/schemas'
import { canEditMatter } from '@/matter/utils/matter-permissions'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import { useAppSelector } from '@/store/store-hooks'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { matterCreatedBeforeLaunch } from '@/analytics/utils/matter-jurisdiction-analytics'

type MatterJurisdictionProps = {
  matter: MatterRead
  onEdit: () => void
}

export function MatterJurisdiction({ matter, onEdit }: MatterJurisdictionProps) {
  const settingsLoaded = useAppSelector(selectSettingsLoaded)
  const { trackEvent } = useAnalytics()
  const jurisdictionLabel = matter.default_jurisdiction ? getJurisdictionLabel(matter.default_jurisdiction) : null
  const jurisdictionKind = inferJurisdictionKind(matter.default_jurisdiction)
  const showMissingJurisdiction = settingsLoaded && !isUnassignedMatter(matter) && !jurisdictionLabel
  const canEdit = canEditMatter(matter)

  // Tracks the CTA impression once per matter shown, rather than once per component mount, since
  // this component stays mounted across in-app matter navigation instead of remounting per matter.
  const ctaViewedMatterIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!showMissingJurisdiction || !canEdit) return
    if (ctaViewedMatterIdRef.current === matter.id) return
    ctaViewedMatterIdRef.current = matter.id
    trackEvent(AnalyticsEvent.MatterJurisdictionCtaViewed, {
      matterId: matter.id,
      matterCreatedBeforeLaunch: matterCreatedBeforeLaunch(matter.created_at),
    })
  }, [matter.id, matter.created_at, showMissingJurisdiction, canEdit, trackEvent])

  if (jurisdictionLabel) {
    return (
      <div className="mt-2 flex items-center gap-1 text-sm font-medium leading-5 tracking-[-0.15px] text-brand-neutral-700">
        <JurisdictionIcon kind={jurisdictionKind} className="size-4 shrink-0" />
        <span>{jurisdictionLabel}</span>
      </div>
    )
  }

  if (showMissingJurisdiction && !canEdit) {
    return <div className="mt-2 text-sm font-normal leading-5 tracking-[-0.15px] text-brand-neutral-600">No Jurisdiction Provided</div>
  }

  if (showMissingJurisdiction && canEdit) {
    return (
      <button
        type="button"
        className="mt-2 inline-flex items-center gap-1.5 rounded text-sm font-medium leading-5 tracking-[-0.15px] text-yellow-800 hocus:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-600"
        onClick={() => {
          trackEvent(AnalyticsEvent.MatterJurisdictionCtaClicked, {
            matterId: matter.id,
            matterCreatedBeforeLaunch: matterCreatedBeforeLaunch(matter.created_at),
          })
          onEdit()
        }}
      >
        <InfoCircleFilled className="size-3.5 shrink-0 text-yellow-600" aria-hidden="true" />
        <span>No Jurisdiction Provided</span>
      </button>
    )
  }

  return null
}
